import { skeletonStyle } from "@repo/ui/Skeletons/skeletons";
import type { FC, ReactElement } from "react";

/**
 * Inline placeholder for a crumb label that's still loading. Sized (with
 * `align-middle`) to sit inside the trail's text line without changing its
 * height, so the swap to the loaded name only reflows horizontally.
 */
export const CrumbLabelSkeleton: FC = () => (
  <span
    aria-hidden
    className={skeletonStyle({
      className: "inline-block h-3 w-20 align-middle",
    })}
  />
);

interface RenderCrumbLabelOptions<Entity> {
  /** Whether the label's route param parsed into a usable id. */
  hasValidId: boolean;
  result: { data?: Entity; isError: boolean };
  buildText: (entity: Entity) => string;
  /**
   * Shown for an invalid id or a failed fetch, so the crumb settles on a
   * generic label instead of showing a skeleton forever.
   */
  fallbackText: string;
}

/** The shared render logic for entity-fetching crumb label components. */
export function renderCrumbLabel<Entity>({
  hasValidId,
  result,
  buildText,
  fallbackText,
}: RenderCrumbLabelOptions<Entity>): ReactElement {
  if (!hasValidId || result.isError) {
    return <>{fallbackText}</>;
  }
  return result.data ? <>{buildText(result.data)}</> : <CrumbLabelSkeleton />;
}
